/**
 * OnboardingNoTenant — friendly landing for cold sign-in.
 *
 * Renders when a user has a valid Supabase session but no app_users row
 * AND `/api/signup-finalize` couldn't resolve a tenant_id from
 * app_metadata, user_metadata, or JWT claim. The previous failure mode
 * was a raw 403 "User has no Interaction Engineering app_users row" exception
 * bubbling from `requireAuth` when the user tried to load any
 * tenant-scoped surface. This page replaces that with the consent-first
 * framing: Interaction Engineering is invitation-only.
 *
 * Architecturally: V1 has only two real entry paths — tenant-admin
 * provisioning (super-admin-gated, manual) and magic-invite link from
 * an existing tenant. There's no self-serve "I'm a new tenant admin"
 * path in V1, so the routing here doesn't try to disambiguate that —
 * it surfaces a clear "find your invite or contact support" framing.
 *
 * Routed from `App.jsx` when `useSession().needsOnboarding === true`.
 *
 * Per /shared/V1_PreLaunch_Punchlist.md item 7 (Magic-Invite Landing UX
 * research): this page is a candidate for the same UX research session.
 * Copy here should be testable; if the research surfaces better framing,
 * route the changes through design.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Wordmark, Callout } from "../components/index.js";
import { supabase } from "../lib/supabase.js";

export default function OnboardingNoTenant() {
  const [signingOut, setSigningOut] = useState(false);

  const signOut = async () => {
    setSigningOut(true);
    await supabase.auth.signOut();
    // Force a clean reload so useSession state resets and the user lands
    // on the signed-out intro at "/".
    window.location.assign("/");
  };

  return (
    <div style={{
      maxWidth: 640,
      margin: "0 auto",
      padding: "48px 24px 80px",
    }}>
      <div style={{ marginBottom: 28 }}>
        <Wordmark scale={1.4}/>
      </div>

      <div style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: 11,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: "var(--ppr-color-accent-coral)",
        marginBottom: 8,
      }}>
        Invitation only
      </div>

      <h1 style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-3xl)",
        color: "var(--ppr-color-accent-navy)",
        letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
        lineHeight: "var(--ppr-typography-line-height-tight)",
        margin: "0 0 16px",
      }}>
        You're signed in, but Interaction Engineering doesn't have you set up yet.
      </h1>

      <p style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-md)",
        lineHeight: "var(--ppr-typography-line-height-relaxed)",
        color: "var(--ppr-color-text-secondary)",
        margin: "0 0 28px",
      }}>
        Interaction Engineering is invitation-only — the account owner sets up your access.
        Sign-in alone doesn't grant access on purpose; without an invite, we
        can't tell which account you belong to or what you're allowed to see.
      </p>

      <Callout.Tip>
        <strong>If you have a magic-invite link in an email:</strong> close this
        tab and click the link in your email. The link does the routing your
        sign-in alone can't do.
      </Callout.Tip>

      <Callout.Remember>
        <strong>If you don't have an invite:</strong> contact whoever
        introduced you to Interaction Engineering. They can send a magic-invite link or add you
        from their admin console. We don't show a self-serve signup path on
        purpose — consent matters, and a randomly-signed-up user can't see
        anyone's Profile.
      </Callout.Remember>

      <Callout.WatchOut>
        <strong>If you think this is an error:</strong> a tenant admin may
        have provisioned your account but not run the finalize step yet. Ask
        them to verify the invite went through, or check their admin console
        for your sign-in email.
      </Callout.WatchOut>

      <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={signOut}
          disabled={signingOut}
          style={primaryButtonStyle}
        >
          {signingOut ? "Signing out…" : "Sign out + start over"}
        </button>
        <Link to="/" style={secondaryButtonStyle}>
          Back to home
        </Link>
      </div>

      <p style={{
        marginTop: 32,
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: "var(--ppr-typography-font-size-xs)",
        color: "var(--ppr-color-text-muted)",
      }}>
        Engineering note: this page renders when a Supabase session exists
        but no Interaction Engineering app_users row could be created. The signup-
        finalize route couldn't resolve a tenant_id from your sign-in
        metadata.
      </p>
    </div>
  );
}

const primaryButtonStyle = {
  padding: "12px 22px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "12px 22px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-accent-navy)",
  textDecoration: "none",
};
